import { useMemo } from "react";
import { useRecoilValue } from "recoil";
import { Ac2AccessDeviceModel } from "src/api/access/productac-types";
import { CctvStreamingDataList } from "src/api/cctv/cctv-types";
import { BaseButton } from "src/components";
import BaseRichTooltip from "src/components/BaseRichTooltip";
import { PagePath } from "src/pages/product/details";
import { globalPartnerState } from "src/recoil/partners/atom";
import { BULLET, roundFloat } from "src/utils/common-util";
import { deviceStatusCheck, isCaution } from "../monitoring-func";
import { ExecuteResultType, MonitoringType } from "../monitoring-types";
import { monitoringTooltipData } from "../MonitoringTooltopData";
import { CctvDeviceType } from "../SpaceMonitoring";
import IotDeviceList from "./IotDeviceList";

type Props = {
  monitoring: MonitoringType[];
  handleRefresh: (id: number) => void;
  buildingId: number;
  date: string;
};

// const alertIcon = "⚠️";
export const alertIcon = <>&#x26A0;&#xFE0F;</>;

const TestMonitoringTable = ({ monitoring, handleRefresh, buildingId, date }: Props) => {
  const partner = useRecoilValue(globalPartnerState);

  const hasCCTV = useMemo(
    () => partner?.features?.some((feature) => feature.name === "FEATURE_CCTV" && feature.active === true),
    [partner?.features],
  );

  const calculateTraffic = (cctvDevice: CctvDeviceType) => {
    const { cctvEventDataList, cctvDevicePartnerList } = cctvDevice;
    const capacity = cctvDevicePartnerList?.[0]?.congestionCapacity;
    const crowd = cctvEventDataList?.find((item) => item.eventType === "crowd");

    if (crowd && typeof crowd.eventData !== "string" && crowd.eventData?.count !== undefined) {
      const currentCount = crowd.eventData?.count;

      if (currentCount >= 0 && capacity) {
        const percentage = (currentCount / capacity) * 100;
        const congestionLevel = percentage < 35 ? "원활" : percentage < 70 ? "보통" : "혼잡";

        // 테이블 셀에 표시될 기본 텍스트
        const displayText = `${congestionLevel} ${currentCount}명 (${Math.round(percentage)}%)`;

        // 툴팁에 표시될 상세 텍스트
        const tooltipText = `${congestionLevel} (${Math.round(percentage)}% = ${currentCount}/${capacity})`;
        return {
          displayText,
          tooltipText,
          level: congestionLevel,
        };
      }
    }

    return {
      displayText: "-",
      tooltipText: "-",
      level: "unknown",
    };
  };

  const temperatureAverage = (passTemperatures: ExecuteResultType[]) => {
    const temperatureList = passTemperatures.map((item) => ({
      id: item.location.identifier,
      internalDisplayName: item.internalDisplayName,
      location: item.location,
      labelType: item.labelType,
      timestamp: item.result?.temperature?.timestamp || "",
      tempValue: (item.result?.temperature && (item.result?.temperature?.value ?? item.result.temperature?.current)) ?? null,
    }));

    // 0.0°C(비정상값) 제외
    const validTemperatures = temperatureList.filter((item) => typeof item.tempValue === "number" && !isNaN(item.tempValue) && item.tempValue !== 0);

    const sum = validTemperatures.reduce((acc, current) => acc + (current.tempValue as number), 0);
    const result = validTemperatures.length > 0 ? sum / validTemperatures.length : null;
    const final = result !== null ? roundFloat(result) : "";

    if (passTemperatures.some((item) => item.isLoading && !date)) {
      return "-";
    }
    return (
      <>
        <BaseRichTooltip
          rightLabel={<p className="font14">{final !== "" && <span>{`${final}°C`}</span>}</p>}
          children={
            <div>
              <p className="popover-gray__title">평균 온도</p>
              <p>
                {BULLET} {final ? final + "°C" : "알수없음"}
              </p>
              {temperatureList.map((item, idx: number) => (
                <div key={`${item.location.category}-${item.labelType}-${idx}`} className="flex-center">
                  <p>{`${BULLET} ${roundFloat(item.tempValue) + "°C"} ${item.internalDisplayName}`}</p>
                  {isCaution(item.timestamp) && <div className="ml3">{alertIcon}</div>}
                </div>
              ))}
            </div>
          }
        />
      </>
    );
  };

  // CCTV 스트리밍 데이터 찾기

  const findCctvStreaming = (cctvDevice: CctvDeviceType) => {
    const streamingData = CctvStreamingDataList.find((data) => data.cameraGroup === cctvDevice?.cameraGroup)?.cameraList.find(
      (camera) => camera.cameraId === cctvDevice?.cameraId,
    );
    return streamingData;
  };

  return (
    <>
      <section className={`table-label ${hasCCTV ? "has-cctv" : ""} `}>
        <div className="table-label__info flex-center">
          <BaseRichTooltip touchIcon="CIRCLE" className="mr10" rightLabel={<p className="font13">활성</p>} status={"ACTIVE"} />
          <BaseRichTooltip touchIcon="CIRCLE" className="mr10" rightLabel={<p className="font13">비활성</p>} status={"INACTIVE"} />
          <BaseRichTooltip touchIcon="CIRCLE" className="mr10" rightLabel={<p className="font13">불가</p>} status={"CANNOT_USE"} />
          <BaseRichTooltip touchIcon="CIRCLE" className="mr10" rightLabel={<p className="font13">확인 필요</p>} status={"CAUTION"} />
          <BaseRichTooltip touchIcon={<p className={`ic-man-b cursor-unset`}></p>} className="mr10" rightLabel={<p className="font13">이용 중</p>} />
          <BaseRichTooltip touchIcon={<p className={`ic-man-g cursor-unset`}></p>} className="mr20" rightLabel={<p className="font13">비어있음</p>} />
          <p className="font13">상태를 클릭하면 정보를 볼 수 있습니다</p>
        </div>
        <div className="table-label__refresh">
          <p>업데이트: {date}</p>
          <BaseButton
            title="새로고침"
            className="size-small color-white"
            onClick={() => {
              handleRefresh(buildingId);
            }}
          />
        </div>
      </section>
      <table className={`table-monitoring ${hasCCTV ? "has-cctv" : ""}`}>
        <thead className="table-monitoring__header">
          <tr>
            <th colSpan={2}>
              <p></p>
            </th>
            <th>
              <p className="text-left pl18">공간</p>
            </th>
            <th>
              <p>출입기기</p>
            </th>
            <th>
              <p>재실</p>
            </th>
            <th>
              <div className="flex-center-center">
                <p className="mr4">실내기 </p>
                <p className="font12">(모드/희망온도)</p>
              </div>
            </th>
            <th>
              <p>평균 온도</p>
            </th>
            <th>
              <p>조명</p>
            </th>
            <th>
              <p>남자 화장실</p>
            </th>
            <th>
              <p>여자 화장실</p>
            </th>
            {hasCCTV && (
              <>
                <th>
                  <p>CCTV</p>
                </th>
                <th>
                  <p>혼잡도</p>
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="table-monitoring__body">
          {monitoring.map((item, i) => (
            <tr className="table-monitoring__body-floor" key={"floor" + i}>
              <td className="floor-td">
                <div className="floor-cell">
                  <p>{item.floorValue}</p>
                </div>
              </td>

              <td colSpan={11}>
                {/* 첫번째 row는 고정 */}
                <section className="table-monitoring__body-reservation">
                  <div className="cell"></div>

                  {/* 출입기기 */}
                  <div className="cell">
                    {item?.floorDevices?.access?.map((device: Ac2AccessDeviceModel & { isLoading?: boolean }, idx: number) => {
                      if (device.isLoading && date) device.isLoading = false;
                      if (device.isLoading && date) device.isLoading = false;
                      return (
                        <BaseRichTooltip
                          key={`floor-${device.id}-${idx}`}
                          touchIcon={
                            device.isLoading ? (
                              <p style={{ height: 20 }} className="flex-center">
                                -
                              </p>
                            ) : (
                              "CIRCLE"
                            )
                          }
                          leftLabel={
                            <p className="font14 text-center" style={{ width: 20 }}>
                              {idx + 1}
                            </p>
                          }
                          status={deviceStatusCheck("ACCESS", device)}
                          children={monitoringTooltipData({ access: device })}
                        />
                      );
                    })}
                  </div>

                  {/* 재실 */}
                  <div className="cell">
                    <IotDeviceList devices={item.floorDevices.sensor} buildingId={String(buildingId)} partnerId={String(partner?.id)} />
                  </div>

                  {/* 실내기 */}
                  <div className="cell">
                    <IotDeviceList devices={item.floorDevices.indoorUnit} buildingId={String(buildingId)} partnerId={String(partner?.id)} />
                  </div>

                  {/* 평균 온도 */}
                  {/* 층 평균온도 = 층 + 층의 공용공간 온도의 평균 */}
                  <div className="cell">
                    <IotDeviceList
                      devices={[
                        ...item.floorDevices.temperature,
                        ...item.facilityList.map((facility) => facility.facilityDevices.temperature).flat(),
                      ]}
                      buildingId={String(buildingId)}
                      partnerId={String(partner?.id)}
                    />

                    {/* {temperatureAverage([
                      ...item.floorDevices.temperature,
                      ...item.facilityList.map((facility) => facility.facilityDevices.temperature).flat(),
                    ])} */}
                  </div>

                  {/* 조명 */}
                  <div className="cell">
                    {item.floorDevices.light.map((device, idx: number) => {
                      return (
                        <div key={`floor-${device.labelType}-${idx}`}>
                          {device.isLoading && <p style={{ height: 20 }}>-</p>}
                          {!device.isLoading && (
                            <BaseRichTooltip
                              key={`facility-${device.labelType}-${idx}`}
                              touchIcon="CIRCLE"
                              rightLabel={<p className="font14"></p>}
                              status={deviceStatusCheck("IOT", device)}
                              children={monitoringTooltipData({ iot: device }, deviceStatusCheck("IOT", device) === "CAUTION")}
                              firstActionLabel="상세보기"
                              firstActionFunction={() => {
                                window.open(
                                  PagePath.building.detail.replace(":id", String(buildingId)).replace(":partnerId", String(partner?.id)) +
                                    `?tab=iotDevice&tabType=detail&tabId=${device.buildingDeviceId}`,
                                );
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* 남자 화장실 */}
                  <div className="cell">
                    <IotDeviceList devices={item.floorDevices.mensToilet} buildingId={String(buildingId)} partnerId={String(partner?.id)} />
                  </div>

                  {/* 여자 화장실 */}
                  <div className="cell">
                    <IotDeviceList devices={item.floorDevices.womensToilet} buildingId={String(buildingId)} partnerId={String(partner?.id)} />
                  </div>

                  {hasCCTV && (
                    <>
                      {/* CCTV */}
                      <div className="cell">
                        {item.floorDevices.cctv?.map((device: CctvDeviceType, idx: number) => {
                          if (device.isLoading && date) device.isLoading = false;
                          return (
                            <div key={`floor-${device.id}-${idx}`}>
                              <BaseRichTooltip
                                touchIcon={
                                  device.isLoading ? (
                                    <p style={{ height: 20 }} className="flex-center">
                                      -
                                    </p>
                                  ) : (
                                    "CIRCLE"
                                  )
                                }
                                status={"ACTIVE"}
                                leftLabel={
                                  <p style={{ width: 20 }} className="font14 text-center">
                                    {idx + 1}
                                  </p>
                                }
                                firstActionLabel="상세보기"
                                firstActionFunction={() => {
                                  window.open(PagePath.cctv.detail.replace(":id", String(device.id)).replace(":partnerId", String(partner?.id)));
                                }}
                                secondActionLabel={findCctvStreaming(device)?.streamingUrl ? "영상보기" : ""}
                                {...(findCctvStreaming(device)?.streamingUrl && {
                                  secondActionFunction: () => {
                                    window.open(findCctvStreaming(device)?.streamingUrl);
                                  },
                                })}
                                children={monitoringTooltipData({ cctv: device })}
                              />
                            </div>
                          );
                        })}
                      </div>

                      {/* 혼잡도 */}
                      <div className="cell px5">
                        {item.floorDevices.cctv?.map((device, idx: number) => {
                          const trafficInfo = calculateTraffic(device);

                          return (
                            <BaseRichTooltip
                              key={`floor-${device.id}-${idx}`}
                              touchIcon={
                                device.isLoading ? (
                                  <p style={{ height: 20 }} className="flex-center">
                                    -
                                  </p>
                                ) : (
                                  "CIRCLE"
                                )
                              }
                              status={
                                trafficInfo.level === "원활"
                                  ? "LIGHT_TRAFFIC"
                                  : trafficInfo.level === "보통"
                                  ? "CAUTION"
                                  : trafficInfo.level === "혼잡"
                                  ? "CAUTION_RED"
                                  : "CANNOT_USE"
                              }
                              rightLabel={!device.isLoading && calculateTraffic(device || []).displayText}
                              firstActionLabel="상세보기"
                              firstActionFunction={() => {
                                window.open(PagePath.cctv.detail.replace(":id", String(device.id)).replace(":partnerId", String(partner?.id)));
                              }}
                              secondActionLabel={findCctvStreaming(device)?.streamingUrl ? "영상보기" : ""}
                              {...(findCctvStreaming(device)?.streamingUrl && {
                                secondActionFunction: () => {
                                  window.open(findCctvStreaming(device)?.streamingUrl);
                                },
                              })}
                              children={
                                <ul>
                                  <p>{`${BULLET} 장치명: ${device.name}`}</p>
                                  <p>{`${BULLET} 혼잡도: ${trafficInfo.tooltipText}`}</p>
                                </ul>
                              }
                            />
                          );
                        })}
                      </div>
                    </>
                  )}
                </section>

                {/* 층 > 회의실 갯수만큼 순회 */}
                {item.facilityList.map((facility, j) => (
                  <section className="table-monitoring__body-reservation" key={"reservation" + j}>
                    {/* 회의실 명 */}
                    <div className="cell">
                      <p className="w-100 text-left">{facility.facilityName}</p>
                    </div>
                    {/* 출입기기 */}
                    <div className="cell">
                      {facility?.facilityDevices.access?.map((device, idx: number) => (
                        <BaseRichTooltip
                          key={`facility-${device.id}-${idx}`}
                          touchIcon="CIRCLE"
                          leftLabel={
                            <p style={{ width: 20 }} className="font14 text-center">
                              {idx + 1}
                            </p>
                          }
                          status={deviceStatusCheck("ACCESS", device)}
                          children={monitoringTooltipData({ access: device })}
                        />
                      ))}
                    </div>

                    {/* 재실 */}
                    <div className="cell">
                      <IotDeviceList devices={facility.facilityDevices.sensor} buildingId={String(buildingId)} partnerId={String(partner?.id)} />
                    </div>
                    {/* 실내기 */}
                    <div className="cell">
                      <IotDeviceList devices={facility.facilityDevices.indoorUnit} buildingId={String(buildingId)} partnerId={String(partner?.id)} />
                    </div>

                    {/* 평균온도 */}

                    <div className="cell">
                      <IotDeviceList devices={facility.facilityDevices.temperature} buildingId={String(buildingId)} partnerId={String(partner?.id)} />

                      {/* {temperatureAverage(facility.facilityDevices.temperature)} */}
                    </div>

                    {/* 조명 */}
                    <div className="cell">
                      <IotDeviceList devices={facility.facilityDevices.light} buildingId={String(buildingId)} partnerId={String(partner?.id)} />
                    </div>
                    {/* 남자 화장실 */}
                    <div className="cell">
                      <IotDeviceList devices={facility.facilityDevices.mensToilet} buildingId={String(buildingId)} partnerId={String(partner?.id)} />
                    </div>
                    {/* 여자 화장실 */}
                    <div className="cell">
                      <IotDeviceList
                        devices={facility.facilityDevices.womensToilet}
                        buildingId={String(buildingId)}
                        partnerId={String(partner?.id)}
                      />
                    </div>
                    {hasCCTV && (
                      <>
                        <div className="cell"></div>

                        {/* 혼잡도 */}
                        <div className="cell"></div>
                      </>
                    )}
                  </section>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default TestMonitoringTable;
